import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EntityDetailDrawer from '../../../EntityDetailDrawer';
import { Code, Field, Section } from '../../../Ontology/DetailPrimitives';
import { PanelError, TierPill } from '../../BenchmarkPrimitives';
import { featureName, humanise, tierLabel, scenarioName } from '../../BenchmarkVocabulary';
import {
    BenchmarkRequestError, CaseDetailResponse, EvidenceStatus, benchmarkService
} from '../../../../services/benchmarkService';
import { SCORE_LAYERS, agentLabel, evidenceMeta, layerLabel, resultOutcomeLabel } from './resultsVocabulary';
import { AgreementBadge } from './SuppliedResultsTable';
import { dsVar, ink } from '../../../../theme/tokens';

interface CaseWhyDrawerProps {
    open: boolean;
    runKey: number | null;
    caseRef: string | null;
    onClose: () => void;
}

/** A layer's or agent's evidence status. Absent evidence is grey, never alarming. */
export const EvidenceStatusChip: React.FC<{ status: EvidenceStatus | null | undefined }> = ({ status }) => {
    const meta = evidenceMeta(status);
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-block', px: 1, py: 0.25, borderRadius: '999px', fontSize: '0.6875rem',
                fontWeight: 600, whiteSpace: 'nowrap', color: ink(meta.color),
                backgroundColor: `${meta.color}14`, border: `1px ${meta.present === false ? 'dashed' : 'solid'} ${meta.color}66`
            }}
        >
            {meta.label}
        </Box>
    );
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box className="flex items-center justify-between gap-3" sx={{ py: 0.75, borderBottom: `1px solid ${dsVar('line')}` }}>
        <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft') }}>{label}</Typography>
        <Box className="flex items-center gap-2 shrink-0">{children}</Box>
    </Box>
);

const contextValue = (value: unknown): string => {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value === null || value === undefined || value === '') return '—';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

const formatDate = (value: string | null | undefined): string | null => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

function kycSourceText(detail: CaseDetailResponse): string {
    if (detail.kycSource === 'supplied') {
        const asOf = formatDate(detail.kycAsOf);
        return `From the KYC file you supplied${asOf ? `, dated ${asOf}` : ''}.`;
    }
    if (detail.kycSource === 'customer_master') {
        return "From the platform's customer master — your KYC file had no row for this customer.";
    }
    return "No KYC was supplied with this sample, so the platform used its customer master where it had one.";
}

/**
 * Why one customer case got its tier: the points each scoring layer added,
 * whether each layer and agent found evidence, the KYC that was used and where
 * it came from, and the alerts the case covers.
 *
 * A layer or agent that found nothing is shown as "no evidence" — the signal
 * was absent and scored zero. It is not a finding in the customer's favour, and
 * the reader should be able to see the difference between "clean" and "unknown".
 */
const CaseWhyDrawer: React.FC<CaseWhyDrawerProps> = ({ open, runKey, caseRef, onClose }) => {
    const [detail, setDetail] = useState<CaseDetailResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!open || !runKey || !caseRef) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setDetail(null);
        benchmarkService.getCaseDetail(runKey, caseRef)
            .then((data) => { if (!cancelled) setDetail(data); })
            .catch((err) => {
                if (!cancelled) setError(err instanceof BenchmarkRequestError ? err.message : 'Could not load this case.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, runKey, caseRef, attempt]);

    const status = detail?.componentStatus ?? {};
    const agents = Object.entries(status.agents ?? {});
    const points = (layer: string) => {
        const value = detail?.scoreBreakdown?.[layer];
        return typeof value === 'number' ? value : null;
    };
    const context = Object.entries(detail?.subjectContext ?? {});
    const override = detail?.hardOverride ?? (detail?.scoreBreakdown?.hard_override as string | null | undefined) ?? null;

    return (
        <EntityDetailDrawer
            open={open && Boolean(caseRef)}
            onClose={onClose}
            docked
            dockedMinWidth={1280}
            icon={<HelpOutlineIcon />}
            subtitle="Why this tier"
            title={caseRef ?? ''}
            description={detail
                ? `The platform gave this customer case **${detail.platformTier ? tierLabel(detail.platformTier) : 'no tier'}**${detail.platformScore !== null ? `, with a score of **${detail.platformScore}**` : ''}. Every alert in the case shares that tier.`
                : ''}
            banner={detail ? (
                <Box className="flex flex-wrap items-center gap-2 mb-3">
                    {detail.institutionTier && <TierPill code={detail.institutionTier} />}
                    {detail.institutionTier && <Typography component="span" sx={{ color: dsVar('fg-muted'), fontSize: '0.875rem' }}>→</Typography>}
                    {detail.platformTier && <TierPill code={detail.platformTier} />}
                    {detail.outcomeClass && (
                        <Typography component="span" sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>
                            {resultOutcomeLabel(detail.outcomeClass)}
                        </Typography>
                    )}
                </Box>
            ) : undefined}
        >
            {loading && (
                <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} sx={{ color: dsVar('accent-fg') }} /></Box>
            )}
            {error && <PanelError message={error} onRetry={() => setAttempt((n) => n + 1)} />}

            {detail && (
                <>
                    <Section title="Points per layer">
                        {SCORE_LAYERS.map((layer) => (
                            <Row key={layer} label={layerLabel(layer)}>
                                <EvidenceStatusChip status={status[layer] as EvidenceStatus | undefined} />
                                <Typography component="span" sx={{ fontSize: '0.8125rem', fontWeight: 700, color: dsVar('fg-body'), minWidth: 64, textAlign: 'right' }}>
                                    {points(layer) === null ? '—' : `${points(layer)} pts`}
                                </Typography>
                            </Row>
                        ))}
                        {override && (
                            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '8px', border: '1px solid rgba(249, 115, 22, 0.4)', backgroundColor: 'rgba(249, 115, 22, 0.08)' }}>
                                <Typography sx={{ fontSize: '0.8125rem', color: ink('#FDBA74') }}>
                                    A hard override set this tier ({humanise(String(override))})
                                    {detail.platformTierWithoutOverride
                                        ? `; the score alone would have given ${tierLabel(detail.platformTierWithoutOverride)}.`
                                        : '.'}
                                </Typography>
                            </Box>
                        )}
                    </Section>

                    {agents.length > 0 && (
                        <Section title="Evidence each agent found">
                            {agents.map(([agent, agentStatus]) => (
                                <Row key={agent} label={agentLabel(agent)}>
                                    <EvidenceStatusChip status={agentStatus} />
                                </Row>
                            ))}
                        </Section>
                    )}

                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mt: 1.5, lineHeight: 1.6 }}>
                        "No evidence" means the signal was absent, so it added no points. It is not evidence that the customer is low-risk.
                    </Typography>

                    <Section title="KYC used" always>
                        <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), mb: 1.25, lineHeight: 1.6 }}>
                            {kycSourceText(detail)}
                        </Typography>
                        {context.length > 0 && (
                            <>
                                <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mb: 0.75 }}>
                                    Customer risk context — recorded, not scored
                                </Typography>
                                {context.map(([key, value]) => (
                                    <Field key={key} label={featureName(key)} value={contextValue(value)} />
                                ))}
                            </>
                        )}
                    </Section>

                    <Section title={`Alerts in this case (${detail.alerts.length})`}>
                        {detail.asOf && (
                            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mb: 1 }}>
                                Assessed as of {formatDate(detail.asOf)}
                            </Typography>
                        )}
                        {detail.alerts.map((alert) => (
                            <Box key={alert.alertRef} sx={{ py: 1, borderBottom: `1px solid ${dsVar('line')}` }}>
                                <Box className="flex items-center justify-between gap-2">
                                    <Code>{alert.alertRef}</Code>
                                    <AgreementBadge kind={alert.agreement} />
                                </Box>
                                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), mt: 0.5 }}>
                                    {[
                                        alert.scenarioId ? scenarioName(alert.scenarioId) : null,
                                        alert.institutionTierRaw ? `your tier ${alert.institutionTierRaw}` : null,
                                        resultOutcomeLabel(alert.disposition),
                                        alert.sarFiled ? 'SAR filed' : null
                                    ].filter(Boolean).join(' · ')}
                                </Typography>
                            </Box>
                        ))}
                    </Section>
                </>
            )}
        </EntityDetailDrawer>
    );
};

export default CaseWhyDrawer;
